import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import AppBarPrimaryButton from 'components/shared/buttons/AppBarPrimaryButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import SessionCreateDialog from 'components/session/dialog/create/SessionCreateDialog';
import SessionDetailsDialog from 'components/session/dialog/details/SessionDetailsDialog';
import SessionDrawer from 'components/session/calendar/SessionDrawer';
import SessionHint from 'components/session/calendar/SessionHint';
import CondensedWeekAgenda from 'components/session/calendar/CondensedWeekAgenda';
import GroupSessionDetailsDialog from 'components/session/dialog/details/GroupSessionDetailsDialog';
import NoActivePlanModal from 'components/shared/modals/NoActivePlanModal';
import GroupSessionEvent from 'components/shared/calendar-events/GroupSessionEvent';
import GroupSessionWeeklyEvent from 'components/shared/calendar-events/GroupSessionWeeklyEvent';
import MonthlySessionEvent from 'components/shared/calendar-events/MonthlySessionEvent';
import WeeklySessionEvent from 'components/shared/calendar-events/WeeklySessionEvent';
import { userAvailableSessionsFields } from 'common/constants/query-fields/user';
import {
  compactSlotLabelContent,
  eventTimeFormat,
  headerToolbar,
  slotLabelFormat,
  titleFormat
} from 'common/constants/session';
import { GOLD_PULSE_DURATION_MS } from 'common/theme/animations/goldPulse';
import requestStatus from 'common/constants/status';
import { differenceInDays, parseISO } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { getInteractionType } from 'common/utils/general';
import { canClientCreateSessions } from 'common/utils/session';
import { isValidId } from 'common/utils/id';
import { isAdmin, isClient, isTrainer } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import LayoutContext from 'context/LayoutContext';
import useSessionCalendar from 'hooks/session/useSessionCalendar';
import authSlice from 'store/modules/auth';
import clientPaymentSlice from 'store/modules/clientPayment';
import groupSessionSlice from 'store/modules/groupSession';
import playWorkoutSlice, { selectWorkoutActive } from 'store/modules/playWorkout';
import { MY_WEEKLY_ROUTINE, MY_PLAY_WORKOUTS } from 'api/graphql/play';
import { buildWorkoutPlan } from 'components/play/workoutEstimate';
import { primaryMuscleOf } from 'components/play/constants';
import routes from 'common/routes';

function SessionCalendar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('session.calendar', { ns: 'routes' });
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const { setCompactLayout, setNoBgColorLayout } = useContext(LayoutContext);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedGroupEvent, setSelectedGroupEvent] = useState(null);
  const [noPlanModalOpen, setNoPlanModalOpen] = useState(false);

  const availableSessions = useSelector((state) => state.auth.user?.available_sessions) ?? 0;
  const eventStatus = useSelector((state) => state.session.status.event);
  const rawIsActivePlus = useSelector((state) => state.auth.user.is_active_plus);
  const membershipStatus = useSelector((state) => state.auth.user?.membership_status);
  const role = useSelector((state) => state.auth.user.role);
  const platform = useSelector((state) => state.auth.user.platform);
  const hasOneOnOne = useSelector((state) => state.auth.user.has_one_on_one_sessions);
  const workoutActive = useSelector(selectWorkoutActive);
  const isOtrixPlay = platform === 'otrix_play';
  const sessions = useSelector((state) => state.session.data.events);
  const sessionStatus = useSelector((state) => state.session.status.general);
  const userId = Number(useSelector((state) => state.auth.user.id) ?? 0);
  const activePayments = useSelector((state) => state.clientPayment.data.activePayments) || [];
  const myGroupSessions = useSelector((state) => state.groupSession.data.myGroupSessions);
  const adminGroupSessions = useSelector((state) => state.groupSession.data.list);
  const groupSessionRefresh = useSelector((state) => state.groupSession.refresh);

  const isAdminUser = isAdmin(role);
  const isClientUser = isClient(role);
  const isTrainerUser = isTrainer(role);
  // Active Plus es SOLO una condición de CLIENTE. Un entrenador/admin (aunque tenga el flag por ser
  // también miembro) nunca debe tomar la experiencia de cliente Active Plus: vería grupales de otros
  // entrenadores (myGroupSessions sin filtrar) y el banner Active Plus, y perdería sus uno-a-uno.
  const isActivePlus = rawIsActivePlus && isClientUser;
  const activePlusEndDate = useSelector((state) => state.auth.user.active_plus_end_date ?? null);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  // TODOS los clientes (incl. Active Plus) usan la agenda CONDENSADA nueva. Admin/entrenador → FullCalendar.
  const useCondensed = isClientUser;
  const activePlusDaysLeft = isActivePlus && activePlusEndDate
    ? Math.max(0, differenceInDays(parseISO(activePlusEndDate), new Date()))
    : 0;

  const hasNonActivePlusSessions = isActivePlus && activePayments.some((p) => {
    const name = p.plan_id?.name?.toLowerCase().replace(/\s+/g, '') || '';
    return !name.includes('activeplus') && p.sessions_remaining > 0;
  });

  // OTRIX Play: la agenda muestra la SEMANA de la rutina como cards por día (además de las
  // sesiones 1:1 si el usuario tiene un plan que las otorgue). La rutina ya está persistida
  // (weekly_routine), así que aquí solo se superpone — no se escribe en la tabla de sesiones.
  const { data: routineData } = useQuery(MY_WEEKLY_ROUTINE, {
    skip: !isOtrixPlay,
    fetchPolicy: 'cache-and-network'
  });

  // Entrenamientos registrados (fecha real): pintan en la tira el día completado + kcal.
  const { data: workoutsData } = useQuery(MY_PLAY_WORKOUTS, {
    variables: { first: 60 },
    skip: !isOtrixPlay,
    fetchPolicy: 'cache-and-network'
  });

  const routineByDay = useMemo(() => {
    if (!isOtrixPlay) return null;
    const map = {};
    // Sin `exercise` no hay nada que pintar: un ejercicio borrado del catálogo (soft delete) deja
    // su item de rutina en pie con la relación en null. Ver el filtro gemelo de WeeklyRoutineBuilder.
    (routineData?.myWeeklyRoutine?.items || [])
      .filter((item) => item.exercise)
      .forEach((item) => {
        (map[item.day_of_week] = map[item.day_of_week] || []).push(item);
      });
    Object.keys(map).forEach((day) => map[day].sort((a, b) => (a.position || 0) - (b.position || 0)));
    return map;
  }, [isOtrixPlay, routineData]);

  // Arranca el motor de ejecución para el día tocado (no pisa una sesión ya en curso).
  const handleStartRoutine = useCallback(
    (dayOfWeek, dayItems) => {
      if (!workoutActive) {
        dispatch(
          playWorkoutSlice.actions.start({
            dayOfWeek,
            muscleFocus: primaryMuscleOf(dayItems),
            exercises: buildWorkoutPlan(dayItems, routineData?.myWeeklyRoutine?.progression_week || 1),
            now: Date.now(),
            progressionWeek: routineData?.myWeeklyRoutine?.progression_week || null
          })
        );
      }
      navigate(routes.playWorkout.path);
    },
    [workoutActive, dispatch, navigate, routineData]
  );

  const {
    calendarRef,
    handleDateClick,
    handleDateSet,
    isDialogOpen,
    moreLinkContent,
    openDrawer,
    todaySessions,
    toggleDrawer,
    toggleSessionCreateDialog
  } = useSessionCalendar();

  // Parpadeo dorado (encendido/apagado) de la columna de un día (al saltar de MES→SEMANA o por
  // deep-link). Mismo efecto que la agenda condensada del cliente.
  const pulseDayColumn = useCallback((dateStr) => {
    setTimeout(() => {
      const root = calendarRef.current?.elRef?.current || document;
      const cols = root.querySelectorAll(`.fc-timegrid-col[data-date="${dateStr}"]`);
      cols.forEach((col) => {
        col.classList.add('fc-otx-day-pulse');
        setTimeout(() => col.classList.remove('fc-otx-day-pulse'), GOLD_PULSE_DURATION_MS);
      });
    }, 60);
  }, [calendarRef]);

  // En la vista de MES, un clic en el día lleva a la SEMANA con su columna parpadeando; en semana,
  // el clic crea sesión (mismo gate Active Plus que antes).
  const onDateClick = useCallback((info) => {
    if (info.view.type === 'dayGridMonth') {
      const api = calendarRef.current?.getApi();
      if (api) {
        api.changeView('timeGridWeek', info.dateStr);
        pulseDayColumn(info.dateStr);
      }
      return;
    }
    if (!isActivePlus || hasNonActivePlusSessions) handleDateClick(info);
  }, [calendarRef, handleDateClick, pulseDayColumn, isActivePlus, hasNonActivePlusSessions]);

  const toGroupCalendarEvent = useCallback(
    (session) => ({
      id: String(session.id),
      start: session.start_date,
      end: session.end_date,
      title: session.session_type_id?.name ?? 'Sesión grupal',
      extendedProps: {
        isGroupSession: true,
        trainer: session.trainer,
        sessionTypeName: session.session_type_id?.name ?? '',
        status: session.status
      }
    }),
    []
  );

  // Active Plus client: my group sessions
  const groupSessionEvents = useMemo(
    () => myGroupSessions.map(toGroupCalendarEvent),
    [myGroupSessions, toGroupCalendarEvent]
  );

  // Admin: merge regular sessions + group sessions
  const adminGroupSessionEvents = useMemo(
    () => adminGroupSessions.map(toGroupCalendarEvent),
    [adminGroupSessions, toGroupCalendarEvent]
  );

  const calendarEvents = useMemo(() => {
    if (isAdminUser || isTrainerUser) return [...sessions, ...adminGroupSessionEvents];
    // Cliente: SIEMPRE incluir sus uno-a-uno (historial pasado atenuado + futuras) además de las
    // grupales Active Plus. Antes el Active Plus "puro" recibía solo grupales → calendario vacío.
    if (isActivePlus) return [...sessions, ...groupSessionEvents];
    return sessions;
  }, [isActivePlus, isAdminUser, isTrainerUser, sessions, groupSessionEvents, adminGroupSessionEvents]);

  // Vista semanal COMPACTA: el rango de horas (slotMinTime/slotMaxTime) se ajusta a las horas que
  // TIENEN sesiones en la semana visible. La altura se calcula para que cada hora sea un slot ALTO
  // (las cards llenan su franja, como el modal) y, MUY importante, se la pasamos a FullCalendar vía
  // `height` + `expandRows` para que ÉL posicione los eventos correctamente (forzar la altura del
  // slot por CSS desalinea los eventos).
  const { slotMinTime, slotMaxTime, calendarHeight } = useMemo(() => {
    const pad = (n) => String(n).padStart(2, '0');
    let min;
    let max;
    if (!isClientUser) {
      // Admin/entrenador: rango COMPLETO fijo (necesitan ver todas las horas del horario).
      min = 4;
      max = 21;
    } else {
      // Cliente: vista compacta (solo las horas que tienen sesión en la semana visible).
      const hours = [];
      calendarEvents.forEach((e) => {
        const s = e.start ? new Date(e.start) : null;
        const end = e.end ? new Date(e.end) : null;
        if (s && Number.isFinite(s.getHours())) hours.push(s.getHours());
        if (end && Number.isFinite(end.getHours())) hours.push(end.getMinutes() > 0 ? end.getHours() + 1 : end.getHours());
      });
      if (hours.length === 0) {
        // Sin sesiones en la semana: ventana corta por defecto (la altura mínima evita que se vea rota).
        min = 6;
        max = 10;
      } else {
        // SOLO el rango con sesiones: de la primera hora a la última (sin relleno arriba/abajo).
        min = Math.max(0, Math.min(...hours));
        max = Math.min(24, Math.max(...hours));
        if (max <= min) max = Math.min(24, min + 1);
      }
    }
    const slotCount = Math.max(1, max - min);
    // Franjas altas: con expandRows, FullCalendar las estira para llenar `height`, así una sola
    // sesión se ve como un bloque grande (no contraído). El cliente ve pocas horas → más altas.
    let slotPx = 74;
    if (isExtraSmall) slotPx = 64;
    else if (isClientUser) slotPx = 110;
    // Altura MÍNIMA generosa para que NUNCA se contraiga (1 sesión / 0 sesiones).
    const minHeight = isExtraSmall ? 360 : 460;
    return {
      slotMinTime: `${pad(min)}:00:00`,
      slotMaxTime: `${pad(max)}:00:00`,
      calendarHeight: Math.max(minHeight, 70 + slotCount * slotPx)
    };
  }, [calendarEvents, isExtraSmall, isClientUser]);


  // Deep-link desde "Ir a agenda" del detalle de sesión: ?date=YYYY-MM-DD&session=ID →
  // posiciona el calendario en esa fecha y abre el modal de detalle de esa sesión UNA sola vez.
  // El ref evita el loop: al cerrar el modal se refetchean eventos y el efecto se re-ejecuta;
  // como ya marcamos este deep-link como manejado, no vuelve a abrirlo.
  const location = useLocation();
  const handledDeepLink = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    const sessionParam = params.get('session');
    const key = `${dateParam}:${sessionParam}`;
    if (!dateParam || !calendarRef.current || handledDeepLink.current === key) return;

    const api = calendarRef.current.getApi();
    api.gotoDate(dateParam);
    // Parpadeo dorado del día al llegar desde "Sesiones" de disponibilidad / "Ir a agenda".
    if (api.view?.type !== 'dayGridMonth') pulseDayColumn(dateParam);

    // `?session=undefined` llega como la CADENA "undefined", que es verdadera, así que pasaba el
    // filtro y se quedaba buscando un evento que no existe: nunca marcaba el enlace como atendido
    // y el efecto se repetía con cada recarga de eventos, en bucle. Un id inservible se trata como
    // si no viniera.
    if (!isValidId(sessionParam)) {
      handledDeepLink.current = key;
      return;
    }

    // Busca el evento; si los eventos aún no cargaron, NO marca como manejado → reintenta
    // cuando cambie calendarEvents. Solo marca (y deja de reintentar) cuando lo abre.
    const ev = api.getEventById(String(sessionParam));
    if (ev) {
      if (ev.extendedProps?.isGroupSession) setSelectedGroupEvent(ev);
      else setSelectedEvent(ev);
      handledDeepLink.current = key;
    }
  }, [location.search, calendarEvents]);

  // Deep-link para la vista condensada (sin FullCalendar): posiciona la semana (initialDate) y abre
  // la sesión indicada cuando los eventos ya cargaron.
  const condensedDeepLink = useRef(null);
  const deepLinkParams = new URLSearchParams(location.search);
  const deepLinkDateParam = deepLinkParams.get('date');
  const deepLinkSessionParam = deepLinkParams.get('session');
  const deepLinkDate = useMemo(
    () => (deepLinkDateParam ? new Date(`${deepLinkDateParam}T00:00:00`) : null),
    [deepLinkDateParam]
  );
  useEffect(() => {
    // Mismo guard que arriba: sin él, la vista condensada repetía el bucle por su cuenta.
    if (!useCondensed || !isValidId(deepLinkSessionParam)) return;
    const key = `${deepLinkDateParam}:${deepLinkSessionParam}`;
    if (condensedDeepLink.current === key) return;
    const ev = calendarEvents.find((e) => String(e.id) === String(deepLinkSessionParam));
    if (ev) {
      if (ev.extendedProps?.isGroupSession) setSelectedGroupEvent(ev);
      else setSelectedEvent(ev);
      condensedDeepLink.current = key;
    }
  }, [useCondensed, deepLinkSessionParam, deepLinkDateParam, calendarEvents]);

  // Al crear: si el cliente NO puede (mora/inactivo o sin sesiones) → aviso; si puede → form.
  const handleCreateClick = useCallback(() => {
    if (isAdminUser || canClientCreateSessions(role, availableSessions, membershipStatus)) {
      toggleSessionCreateDialog();
    } else {
      setNoPlanModalOpen(true);
    }
  }, [isAdminUser, role, availableSessions, membershipStatus, toggleSessionCreateDialog]);

  const appBarButton = useMemo(() => {
    // OTRIX Play: la agenda es su SEMANA de rutina, no sesiones 1:1 → sin "Crear sesión"
    // (salvo que un plan adicional le otorgue uno-a-uno).
    if (isOtrixPlay && !hasOneOnOne) return null;
    // Active Plus PURO no crea sesiones 1-a-1; entrenadores/supervisores tampoco desde aquí.
    if (isActivePlus && !hasNonActivePlusSessions) return null;
    if (!isAdminUser && !isClientUser) return null;

    const isDisabledOrLoading =
      eventStatus === requestStatus.loading || sessionStatus === requestStatus.loading;

    // El botón se muestra siempre (admin/cliente); el click decide form vs aviso (mora/sin sesiones).
    return (
      <AppBarPrimaryButton
        disabled={isDisabledOrLoading}
        label={t('session.create', { ns: 'buttons' })}
        loading={isDisabledOrLoading}
        onClick={handleCreateClick}
      />
    );
  }, [eventStatus, hasNonActivePlusSessions, isActivePlus, isAdminUser, isClientUser, isOtrixPlay, hasOneOnOne, sessionStatus, t, handleCreateClick]);

  useEffect(() => {
    setAppBarButton(appBarButton);
    setAppBarTitle(pageTitle);
  }, [appBarButton, pageTitle, setAppBarButton, setAppBarTitle]);

  useEffect(() => {
    setCompactLayout(true);
    setNoBgColorLayout(true);

    return () => {
      setCompactLayout(false);
      setNoBgColorLayout(false);
    };
  }, []);

  useEffect(() => {
    if (isActivePlus) {
      dispatch(groupSessionSlice.actions.fetchMyGroupSessions());
    } else if (isAdminUser) {
      dispatch(groupSessionSlice.actions.list({ pagination: { first: 100, page: 1 } }));
    } else if (isTrainerUser && userId) {
      dispatch(
        groupSessionSlice.actions.list({
          pagination: { first: 100, page: 1 },
          filters: { trainer_id: [{ value: userId }] }
        })
      );
    } else if (isClientUser) {
      dispatch(authSlice.actions.me(userAvailableSessionsFields));
      dispatch(clientPaymentSlice.actions.getLatest(userId));
    }
  }, [dispatch, isActivePlus, isAdminUser, isClientUser, isTrainerUser, userId]);

  useEffect(() => {
    // Solo los CLIENTES piden sus propios clientPayments (la policy viewAny no permite entrenadores).
    if (isActivePlus && isClientUser && userId) {
      dispatch(clientPaymentSlice.actions.getActivePayments(userId));
    }
  }, [dispatch, isActivePlus, isClientUser, userId]);

  // Refrescar sesiones grupales en la agenda cuando cambien (crear/actualizar/borrar)
  useEffect(() => {
    if (!groupSessionRefresh) return;
    if (isActivePlus) {
      dispatch(groupSessionSlice.actions.fetchMyGroupSessions());
    } else if (isAdminUser) {
      dispatch(groupSessionSlice.actions.list({ pagination: { first: 100, page: 1 } }));
    } else if (isTrainerUser && userId) {
      dispatch(
        groupSessionSlice.actions.list({
          pagination: { first: 100, page: 1 },
          filters: { trainer_id: [{ value: userId }] }
        })
      );
    }
  }, [groupSessionRefresh]);

  const handleCloseDetailsDialog = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const handleCloseGroupDetailsDialog = useCallback(() => {
    setSelectedGroupEvent(null);
  }, []);

  const handleEventClick = (info) => {
    if (info.event.extendedProps.isGroupSession) {
      setSelectedGroupEvent(info.event);
    } else if (!isActivePlus || hasNonActivePlusSessions) {
      setSelectedEvent(info.event);
    }
  };

  const renderMonthEventContent = (info) => {
    if (info.event.extendedProps.isGroupSession) {
      return <GroupSessionEvent event={info.event} />;
    }
    return <MonthlySessionEvent event={info.event} viewType={role} />;
  };

  const renderWeekEventContent = (info) => {
    if (info.event.extendedProps.isGroupSession) {
      return <GroupSessionWeeklyEvent event={info.event} />;
    }
    return <WeeklySessionEvent event={info.event} viewType={role} />;
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      {!isActivePlus && (
        <SessionHint
          cookieName="sessionCalendarHint"
          message={t('calendarHint', { action: getInteractionType(), ns: 'sessions' })}
        />
      )}
      {isActivePlus && (
        <SessionHint
          cookieName="activePlusCalendarHint"
          message={t('activePlusUserInfo', { ns: 'sessions', days: activePlusDaysLeft })}
        />
      )}
      {useCondensed ? (
        <CondensedWeekAgenda
          events={calendarEvents}
          dateLocale={dateLocale}
          onEventClick={(ev) => {
            // Las grupales abren el diálogo de grupo (con Zoom); las individuales el de uno-a-uno.
            if (ev.extendedProps?.isGroupSession) setSelectedGroupEvent(ev);
            else setSelectedEvent(ev);
          }}
          initialDate={deepLinkDate}
          routineByDay={routineByDay}
          onStartRoutine={handleStartRoutine}
          playWorkouts={workoutsData?.myPlayWorkouts || []}
        />
      ) : (
      <div className={`fc-session-calendar fc-${role.toLowerCase()}-view`}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          datesSet={(!isActivePlus || hasNonActivePlusSessions) ? handleDateSet : undefined}
          events={calendarEvents}
          eventClick={handleEventClick}
          eventTimeFormat={eventTimeFormat}
          eventOverlap={isAdmin(role)}
          slotEventOverlap={false}
          expandRows
          allDaySlot={false}
          selectMirror
          height={calendarHeight}
          moreLinkClick="week"
          moreLinkContent={moreLinkContent}
          slotDuration="01:00:00"
          selectable={false}
          slotLabelFormat={slotLabelFormat}
          slotLabelContent={compactSlotLabelContent}
          stickyHeaderDates={false}
          headerToolbar={headerToolbar}
          titleFormat={titleFormat}
          buttonText={{
            today: t('today', { ns: 'common' }),
            month: t('month', { ns: 'common' }),
            week: t('week', { ns: 'common' }),
            day: t('day', { count: 1, ns: 'common' }),
            list: t('list', { ns: 'common' })
          }}
          locale={i18n.language}
          dateClick={onDateClick}
          selectLongPressDelay={1}
          timeZone="local"
          views={{
            dayGridMonth: {
              dayMaxEvents: isExtraSmall ? 1 : 2,
              eventContent: renderMonthEventContent
            },
            timeGridWeek: {
              eventContent: renderWeekEventContent,
              // Se muestran TODAS las solapadas (sin "+N más"): el efecto "mazo" (decorateDeck) las
              // apila en vertical en vez del lado-a-lado fragmentado.
              slotMinTime,
              slotMaxTime
            }
          }}
        />
      </div>
      )}
      {(!isActivePlus || hasNonActivePlusSessions) && (
        <SessionDrawer open={openDrawer} onClose={toggleDrawer} sessions={todaySessions} />
      )}
      {(!isActivePlus || hasNonActivePlusSessions) && (
        <SessionCreateDialog open={isDialogOpen} onClose={toggleSessionCreateDialog} />
      )}
      {(!isActivePlus || hasNonActivePlusSessions) && (
        <SessionDetailsDialog
          open={selectedEvent !== null}
          onClose={handleCloseDetailsDialog}
          event={selectedEvent}
          viewType={role}
        />
      )}
      <GroupSessionDetailsDialog
        open={selectedGroupEvent !== null}
        onClose={handleCloseGroupDetailsDialog}
        event={selectedGroupEvent}
        viewType={role}
      />
      <NoActivePlanModal open={noPlanModalOpen} onClose={() => setNoPlanModalOpen(false)} />
    </>
  );
}

export default SessionCalendar;
